import { useState, useEffect } from 'react'
import { Container, Table, Card, Button, Form, Alert, Badge, Spinner, Modal, Pagination } from 'react-bootstrap'
import adminService from '../../services/adminService'

function AdminReports() {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Filters
  const [statusFilter, setStatusFilter] = useState('')
  const [reasonFilter, setReasonFilter] = useState('')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalReports, setTotalReports] = useState(0)

  // Modal state
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedReport, setSelectedReport] = useState(null)
  const [adminNotes, setAdminNotes] = useState('')
  const [newStatus, setNewStatus] = useState('')
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    loadReports()
  }, [currentPage, statusFilter, reasonFilter])

  const loadReports = async () => {
    setLoading(true)
    try {
      const params = {
        page: currentPage,
        per_page: 20
      }
      if (statusFilter) params.status = statusFilter
      if (reasonFilter) params.reason = reasonFilter

      const data = await adminService.getReports(params)
      setReports(data.reports || data)
      setTotalPages(data.total_pages || 1)
      setTotalReports(data.total || data.length || 0)
    } catch (err) {
      setError('Failed to load reports')
      console.error('Failed to load reports:', err)
    } finally {
      setLoading(false)
    }
  }

  const openDetailModal = (report) => {
    setSelectedReport(report)
    setAdminNotes(report.admin_notes || '')
    setNewStatus(report.status)
    setShowDetailModal(true)
  }

  const handleUpdateReport = async () => {
    if (!selectedReport) return

    setUpdating(true)
    setError(null)

    try {
      await adminService.updateReport(selectedReport.id, {
        status: newStatus,
        admin_notes: adminNotes || undefined
      })

      setSuccess('Report updated successfully!')
      setShowDetailModal(false)
      loadReports()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update report')
    } finally {
      setUpdating(false)
    }
  }

  const handleGlobalHide = async () => {
    if (!selectedReport?.scorable_id) return

    setUpdating(true)
    setError(null)

    try {
      await adminService.hideScorable(selectedReport.scorable_id)

      // Also update report status to actioned
      await adminService.updateReport(selectedReport.id, {
        status: 'actioned',
        admin_notes: (adminNotes ? adminNotes + '\n' : '') + 'Content globally hidden.'
      })

      setSuccess('Content globally hidden and report marked as actioned!')
      setShowDetailModal(false)
      loadReports()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to hide content')
    } finally {
      setUpdating(false)
    }
  }

  const getStatusBadge = (status) => {
    const statusInfo = adminService.REPORT_STATUSES.find(s => s.value === status)
    return <Badge bg={statusInfo?.variant || 'secondary'}>{statusInfo?.label || status}</Badge>
  }

  const getReasonLabel = (reason) => {
    const reasonInfo = adminService.REPORT_REASONS.find(r => r.value === reason)
    return reasonInfo?.label || reason
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Container className="mt-4">
      <h2>Report Management</h2>
      <p className="text-muted mb-4">Review and manage content reports from users.</p>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <div className="row">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="">All Statuses</option>
                  {adminService.REPORT_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Filter by Reason</Form.Label>
                <Form.Select
                  value={reasonFilter}
                  onChange={(e) => {
                    setReasonFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="">All Reasons</option>
                  {adminService.REPORT_REASONS.map(reason => (
                    <option key={reason.value} value={reason.value}>{reason.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <Button variant="outline-secondary" onClick={() => {
                setStatusFilter('')
                setReasonFilter('')
                setCurrentPage(1)
              }}>
                Clear Filters
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Reports Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner />
          <p className="text-muted mt-2">Loading reports...</p>
        </div>
      ) : reports.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <p className="text-muted mb-0">No reports found.</p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card>
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Content</th>
                  <th>Reporter</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map(report => (
                  <tr key={report.id}>
                    <td>{report.id}</td>
                    <td>{getStatusBadge(report.status)}</td>
                    <td>{getReasonLabel(report.reason)}</td>
                    <td>
                      <div style={{ maxWidth: '200px' }}>
                        <small className="text-truncate d-block">
                          {report.scorable?.pretty_name || report.scorable?.thing || `Scorable #${report.scorable_id}`}
                        </small>
                      </div>
                    </td>
                    <td>
                      <small>{report.profile?.name || `Profile #${report.profile_id}`}</small>
                    </td>
                    <td><small>{formatDate(report.created)}</small></td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openDetailModal(report)}
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                Showing page {currentPage} of {totalPages} ({totalReports} total reports)
              </small>
              <Pagination className="mb-0">
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const page = Math.max(1, currentPage - 2) + i
                  if (page > totalPages) return null
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Report #{selectedReport?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReport && (
            <>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Status:</strong> {getStatusBadge(selectedReport.status)}
                </div>
                <div className="col-md-6">
                  <strong>Reason:</strong> {getReasonLabel(selectedReport.reason)}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Reported:</strong> {formatDate(selectedReport.created)}
                </div>
                <div className="col-md-6">
                  <strong>Reporter:</strong> {selectedReport.profile?.name || `Profile #${selectedReport.profile_id}`}
                </div>
              </div>

              <Card className="mb-3">
                <Card.Header>Reported Content</Card.Header>
                <Card.Body>
                  {selectedReport.scorable ? (
                    <>
                      <p className="mb-1">
                        <strong>{selectedReport.scorable.pretty_name || 'Unnamed'}</strong>
                      </p>
                      <p className="mb-1">
                        <a href={selectedReport.scorable.thing} target="_blank" rel="noopener noreferrer">
                          {selectedReport.scorable.thing}
                        </a>
                      </p>
                      {selectedReport.scorable.summary && (
                        <p className="text-muted small">{selectedReport.scorable.summary}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-muted">Scorable #{selectedReport.scorable_id}</p>
                  )}
                </Card.Body>
              </Card>

              {selectedReport.details && (
                <Card className="mb-3">
                  <Card.Header>Reporter's Details</Card.Header>
                  <Card.Body>
                    <p className="mb-0">{selectedReport.details}</p>
                  </Card.Body>
                </Card>
              )}

              <hr />

              <Form.Group className="mb-3">
                <Form.Label>Update Status</Form.Label>
                <Form.Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  {adminService.REPORT_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Admin Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this report..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          <Button
            variant="danger"
            onClick={handleGlobalHide}
            disabled={updating}
          >
            {updating ? 'Processing...' : 'Globally Hide Content'}
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateReport}
            disabled={updating}
          >
            {updating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default AdminReports
